import React from 'react';
import { View, ScrollView } from 'react-native';
import { MenuItem } from './components/MenuItem';
import { Logo } from './components/Logo';
import { SideBarLayoutProps } from './types';
import { useSideBarCollapse, useSideBarMenuItems } from './hooks';
import { UserInfo } from './components/UserInfo';
import { parseDimension } from '../util.style';

const SideBarLayout: React.FC<SideBarLayoutProps> = (props) => {
  const {
    attributes,
    containerStyle,
    width: canvasWidth,
    height: canvasHeight,
    userName: propsUserName = 'User Name',
    isCanvasMode = false,
    position = 'left',
    backgroundColor = '#2c3e50',
    textColor = '#ffffff',
    activeColor = '#3498db',
    hoverColor = '#34495e',
    onMenuItemPress,
    logoText = 'Click',
    logoHeight = 60,
    logoImage,
    userEmail: propsUserEmail = 'mail@sample.com',
    safeAreaPaddingTop = 0,
  } = props;

  // Resolve userLogin bindings if they're still in array/object form
  const userName = propsUserName;
  const userEmail = propsUserEmail;
  // Force desktop behavior - no responsive layout for Sidebar
  const effectiveWidth = 1200; // Always desktop width
  const isDesktop = true;
  const isTablet = false;
  const isMobile = false;

  const { collapsed } = useSideBarCollapse(
    isCanvasMode ? props.collapsed || false : false // Never auto-collapse
  );

  // When used in Header mobile menu, completely bypass responsive logic and use provided menuItems
  const menuItemsFromHook = useSideBarMenuItems(attributes || {});

  // Always prioritize props.menuItems when provided (e.g., from Header)
  const menuItems =
    props.menuItems && props.menuItems.length > 0
      ? props.menuItems
      : menuItemsFromHook.menuItems;

  const getResponsiveHeight = () => {
    if (isDesktop) return containerStyle?.height || 791;
    if (isTablet) return containerStyle?.height || 1022;
    return containerStyle?.height || 256;
  };

  const getFirstGroupFirstItem = () => {
    for (const item of menuItems) {
      if (item.type === 'group' && item.children && item.children.length > 0) {
        return item.children[0].id;
      }
      // If it's not a group, use the first item
      if (item.type !== 'group') {
        return item.id;
      }
    }
    return undefined;
  };

  const activeItemId = isCanvasMode ? getFirstGroupFirstItem() : undefined;

  const effectiveCollapsed =
    isCanvasMode && props.collapsed !== undefined ? props.collapsed : collapsed;

  const getSideBarWidth = () => {
    if (isCanvasMode) {
      return parseDimension(
        canvasWidth || (effectiveCollapsed ? 64 : containerStyle?.width || 256)
      );
    }
    if (effectiveCollapsed) return 64;
    if (isMobile)
      return parseDimension(
        Math.min(effectiveWidth * 0.8, containerStyle?.width || 280)
      );
    if (isTablet) return parseDimension(containerStyle?.width || 240);
    return parseDimension(containerStyle?.width || 256);
  };

  const sideBarWidth = getSideBarWidth();

  // Get effective height using responsive logic
  const getSideBarHeight = () => {
    // If a specific height is passed (like from Header), use it first
    if (canvasHeight) {
      return parseDimension(canvasHeight);
    }

    if (isCanvasMode) {
      // For canvas mode, use canvas height or responsive height
      return parseDimension(canvasHeight || getResponsiveHeight());
    }
    // For non-canvas mode, use full height unless specific height is set
    return parseDimension(containerStyle?.height || '100dvh');
  };

  const sideBarHeight = getSideBarHeight();

  // Container styling - always vertical layout
  const sideBarStyle: Record<string, any> = {
    backgroundColor: containerStyle?.backgroundColor || backgroundColor,
    width: parseDimension(sideBarWidth),
    height: parseDimension(sideBarHeight),
    flexDirection: 'column' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'stretch' as const,
    zIndex: containerStyle?.zIndex || 1000,
    position: 'absolute' as const,
    top: undefined,
    left: position === 'left' ? 0 : undefined,
    right: position === 'right' ? 0 : undefined,
    ...(props.layoutMode === 'float' && {
      position: 'fixed' as const,
      zIndex: 9999,
    }),
  };

  const scrollContainerStyle = {
    flex: 1,
    paddingVertical: 8,
  };

  const renderLogoAndUserHeader = () => {
    // Get logo data from attributes (following Header/Footer pattern)
    const { logo } = attributes || {};
    const { imageUrl, imageLogo, enabled } = logo ?? {};

    // Handle brand logo URL extraction (comprehensive pattern from Footer)
    let effectiveLogoImage = logoImage || ''; // Use prop first
    if (!effectiveLogoImage) {
      if (imageLogo?.binding?.url) {
        // Brand logo from app styles (binding.url)
        effectiveLogoImage = imageLogo.binding.url;
      } else if (imageLogo?.binding?.value?.url) {
        // Brand logo from app styles (binding.value.url)
        effectiveLogoImage = imageLogo.binding.value.url;
      } else if (imageLogo?.url) {
        // Direct URL on imageLogo
        effectiveLogoImage = imageLogo.url;
      } else if (imageUrl) {
        // Regular image URL
        effectiveLogoImage = imageUrl;
      } else if (typeof imageLogo === 'string') {
        // Direct string URL
        effectiveLogoImage = imageLogo;
      }
    }

    // Use props or fallback to attributes
    const effectiveLogoText =
      logoText ||
      attributes?.logo?.logoText ||
      attributes?.logo?.text ||
      'Click';
    const effectiveUserName = userName || '田中 太郎';
    const effectiveUserEmail = userEmail || 'mail@sample.com';

    // Check if logo is enabled (default to true if not specified)
    const isLogoEnabled = enabled !== false;

    return (
      <View
        style={{
          paddingHorizontal: 16,
          paddingVertical: 16,
        }}
      >
        {/* Logo Section - only render if enabled */}
        {isLogoEnabled && (
          <View
            style={{
              marginBottom: 16,
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            <Logo
              isCollapsed={false}
              textColor={textColor}
              logoImage={effectiveLogoImage}
              logoText={effectiveLogoText}
              logoWidth={sideBarWidth as any} // Full width minus padding
              logoHeight={logoHeight}
            />
          </View>
        )}

        {/* User Info Section */}
        <View
          style={{
            alignItems: 'flex-start',
          }}
        >
          <UserInfo
            isCollapsed={false}
            textColor={textColor}
            userName={effectiveUserName}
            email={effectiveUserEmail}
            isCanvasMode={isCanvasMode}
          />
        </View>
      </View>
    );
  };

  const renderContent = () => (
    <ScrollView
      style={{
        ...scrollContainerStyle,
      }}
      contentContainerStyle={{
        flexGrow: 1,
        backgroundColor: sideBarStyle.backgroundColor,
      }}
      showsVerticalScrollIndicator={false}
    >
      {menuItems.map((item, index) => (
        <MenuItem
          key={item.id || index}
          item={item}
          isCollapsed={effectiveCollapsed}
          textColor={textColor}
          activeColor={activeColor}
          hoverColor={activeColor}
          activeOpacity={props.activeOpacity || 0.3}
          onPress={onMenuItemPress}
          isCanvasMode={isCanvasMode}
          activeItemId={activeItemId}
        />
      ))}
    </ScrollView>
  );

  return (
    <View style={sideBarStyle} nativeID={`sidebar-container-${props.id}`}>
      <View
        style={{
          flex: 1,
          backgroundColor: sideBarStyle.backgroundColor,
          paddingTop:
            20 +
            (safeAreaPaddingTop != 0
              ? safeAreaPaddingTop + 50
              : safeAreaPaddingTop),
        }}
      >
        {/* Logo and User Header Section */}
        {!effectiveCollapsed && renderLogoAndUserHeader()}

        {renderContent()}
      </View>
    </View>
  );
};

export default SideBarLayout;
